import {
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input'

/**
 * Min / max digit counts for the LOCAL portion (i.e. after the country
 * dial code), drawn from the canonical mobile lengths in
 * libphonenumber metadata. Covers the countries we see most. For
 * countries not in the table we fall back to libphonenumber's own
 * validity check with the generic message.
 *
 * `name` is the natural-English subject phrase used in length
 * messages — `for ${name}`. We hand-write it (rather than driving off
 * an ISO list) so the prose is correct: "the US" / "the UK" / "the
 * Netherlands" instead of "United States" etc.
 */
const COUNTRY_DIGIT_RANGES = {
  IN: { min: 10, max: 10, name: 'India' },
  US: { min: 10, max: 10, name: 'the US' },
  CA: { min: 10, max: 10, name: 'Canada' },
  GB: { min: 10, max: 10, name: 'the UK' },
  AU: { min: 9, max: 9, name: 'Australia' },
  NZ: { min: 8, max: 10, name: 'New Zealand' },
  DE: { min: 10, max: 11, name: 'Germany' },
  FR: { min: 9, max: 9, name: 'France' },
  IT: { min: 9, max: 11, name: 'Italy' },
  ES: { min: 9, max: 9, name: 'Spain' },
  NL: { min: 9, max: 9, name: 'the Netherlands' },
  BE: { min: 9, max: 9, name: 'Belgium' },
  CH: { min: 9, max: 9, name: 'Switzerland' },
  AT: { min: 10, max: 11, name: 'Austria' },
  SE: { min: 9, max: 9, name: 'Sweden' },
  NO: { min: 8, max: 8, name: 'Norway' },
  DK: { min: 8, max: 8, name: 'Denmark' },
  FI: { min: 9, max: 11, name: 'Finland' },
  IE: { min: 9, max: 9, name: 'Ireland' },
  PT: { min: 9, max: 9, name: 'Portugal' },
  GR: { min: 10, max: 10, name: 'Greece' },
  PL: { min: 9, max: 9, name: 'Poland' },
  TR: { min: 10, max: 10, name: 'Turkey' },
  RU: { min: 10, max: 10, name: 'Russia' },
  BR: { min: 10, max: 11, name: 'Brazil' },
  MX: { min: 10, max: 10, name: 'Mexico' },
  AR: { min: 10, max: 10, name: 'Argentina' },
  JP: { min: 9, max: 10, name: 'Japan' },
  KR: { min: 9, max: 11, name: 'South Korea' },
  CN: { min: 11, max: 11, name: 'China' },
  SG: { min: 8, max: 8, name: 'Singapore' },
  MY: { min: 9, max: 10, name: 'Malaysia' },
  TH: { min: 9, max: 9, name: 'Thailand' },
  ID: { min: 9, max: 12, name: 'Indonesia' },
  PH: { min: 10, max: 10, name: 'the Philippines' },
  VN: { min: 9, max: 10, name: 'Vietnam' },
  PK: { min: 10, max: 10, name: 'Pakistan' },
  BD: { min: 10, max: 10, name: 'Bangladesh' },
  AE: { min: 9, max: 9, name: 'the UAE' },
  SA: { min: 9, max: 9, name: 'Saudi Arabia' },
  IL: { min: 9, max: 9, name: 'Israel' },
  EG: { min: 10, max: 10, name: 'Egypt' },
  NG: { min: 10, max: 10, name: 'Nigeria' },
  KE: { min: 9, max: 9, name: 'Kenya' },
  ZA: { min: 9, max: 9, name: 'South Africa' },
}

/**
 * Reverse-lookup table: dial-code string (e.g. `'91'`) →
 * ISO country code (e.g. `'IN'`). Built once at module load from
 * `COUNTRY_DIGIT_RANGES` so we never drift out of sync. First-wins
 * iteration means shared dial codes (US + CA both `'1'`) resolve to
 * whichever country we declared first in `COUNTRY_DIGIT_RANGES`
 * (US, which keeps the prose natural — "for the US").
 */
const COUNTRY_BY_DIAL_CODE = (() => {
  const map = {}
  for (const code of Object.keys(COUNTRY_DIGIT_RANGES)) {
    try {
      const dial = getCountryCallingCode(code)
      if (!map[dial]) map[dial] = code
    } catch {
      // Skip countries whose dial code couldn't be resolved.
    }
  }
  return map
})()

function dialCodeToCountry(dialCode) {
  if (!dialCode) return undefined
  return COUNTRY_BY_DIAL_CODE[String(dialCode)] ?? undefined
}

/**
 * Multi-condition phone validation that returns a specific, country-
 * aware error message. Use this when you want the form to tell the
 * user exactly what's wrong (e.g. "Indian numbers must start with
 * 6-9.") rather than the generic "invalid".
 *
 * Conditions checked, in order:
 *   1. Non-digit characters in the local portion
 *      → "Please enter a valid Contact Number."
 *   2. India (+91) doesn't start with 6-9
 *      → "Indian numbers must start with 6-9."
 *   3. US/Canada (+1) starts with 0 or 1
 *      → "US/Canada area code cannot start with 0 or 1."
 *   4. Local part shorter than country's minimum
 *      → "Contact Number must be at least N digits[ for COUNTRY]."
 *   5. Local part longer than country's maximum
 *      → "Contact Number must not exceed N digits[ for COUNTRY]."
 *   Fallback: libphonenumber's own validity check
 *      → "Please enter a valid Contact Number."
 *
 * Empty / null / undefined values pass — pair with your own
 * `required` check when the field is mandatory.
 *
 * Returns `{ valid: true }` or `{ valid: false, message }`.
 */
export function validatePhone(value) {
  if (value == null || value === '') return { valid: true }
  if (typeof value !== 'string') {
    return { valid: false, message: 'Please enter a valid Contact Number.' }
  }

  // Condition 1 — must be E.164 shape: `+` followed by digits only.
  // Anything else (spaces, dashes, parens, letters, double +, …)
  // means non-digit characters slipped into the local portion.
  if (!/^\+\d+$/.test(value)) {
    return { valid: false, message: 'Please enter a valid Contact Number.' }
  }

  let parsed
  try {
    parsed = parsePhoneNumber(value)
  } catch {
    return { valid: false, message: 'Please enter a valid Contact Number.' }
  }
  if (!parsed) {
    return { valid: false, message: 'Please enter a valid Contact Number.' }
  }

  // `parsed.country` is undefined when the local digits don't match
  // the country's expected pattern (e.g. too short / too long). In
  // that case we still get `parsed.countryCallingCode` and
  // `parsed.nationalNumber` — enough to apply our own length /
  // prefix rules with a country-specific message. Resolve the
  // missing `country` by reverse-looking-up the dial code against
  // our table; ties (US/CA both share `+1`) collapse to whichever
  // entry was declared first in `COUNTRY_DIGIT_RANGES`.
  const country = parsed.country ?? dialCodeToCountry(parsed.countryCallingCode)
  const local = parsed.nationalNumber ?? ''

  // Condition 2 — India: local mobile must start with 6-9.
  if (country === 'IN' && local.length > 0 && !/^[6-9]/.test(local)) {
    return { valid: false, message: 'Indian numbers must start with 6-9.' }
  }

  // Condition 3 — US/Canada (NANP): the area code (first digit of
  // the local portion) can't be 0 or 1.
  if (
    (country === 'US' || country === 'CA') &&
    local.length > 0 &&
    /^[01]/.test(local)
  ) {
    return {
      valid: false,
      message: 'US/Canada area code cannot start with 0 or 1.',
    }
  }

  // Conditions 4 & 5 — length bracket. Only emit a length-specific
  // message for countries in our hand-tuned table; unknown countries
  // fall through to libphonenumber's check below.
  const range = country ? COUNTRY_DIGIT_RANGES[country] : null
  if (range) {
    const nameSuffix = range.name ? ` for ${range.name}` : ''
    if (local.length < range.min) {
      return {
        valid: false,
        message: `Contact Number must be at least ${range.min} digits${nameSuffix}.`,
      }
    }
    if (local.length > range.max) {
      return {
        valid: false,
        message: `Contact Number must not exceed ${range.max} digits${nameSuffix}.`,
      }
    }
  }

  // Final fallback — let libphonenumber catch anything we missed
  // (e.g. valid-shape number that the carrier metadata still rejects,
  // or a country we haven't tabulated). Generic message because we
  // can't give a specific reason here.
  if (!isValidPhoneNumber(value)) {
    return { valid: false, message: 'Please enter a valid Contact Number.' }
  }

  return { valid: true }
}

/**
 * Boolean-only convenience wrapper around `validatePhone` for
 * callers that don't need the message text (e.g. submit-button
 * gating). Equivalent to `validatePhone(v).valid`.
 */
export function isValidPhone(value) {
  return validatePhone(value).valid
}

/**
 * Maximum valid LOCAL (national) digit count for `country`, read from the
 * same `COUNTRY_DIGIT_RANGES` table that powers `validatePhone`. Sharing the
 * table keeps an input that caps typing at this length and the submit-time
 * validator in lock-step. Returns null for countries we haven't tabulated —
 * callers should treat that as "no cap", mirroring the validator's own
 * fall-through to libphonenumber for those countries.
 */
export function maxLocalDigitsForCountry(country) {
  const range = country ? COUNTRY_DIGIT_RANGES[country] : null
  return range ? range.max : null
}
